// Imports => React
import { useMemo, useEffect } from "react";
import { withStore } from "@stores";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { toJS } from "mobx";

// Imports => Assets
import TransactionHero1x from "@assets/images/transaction-hero-1x.png";

// Imports => Constants
import {
  DATETIME_FORMATS,
  KEYS,
  THEMES,
  AcSanitize,
  AcIcon,
  ICONS,
} from "@opp/ui-kit";

// Imports => Utilities
import { AcIsSet, AcUUID } from "@opp/ui-kit";
import { AcFormatCurrencyAmount, AcFormatDate } from "@utils";

// Imports => Atoms
import {
  AcContainer,
  AcRow,
  AcColumn,
  AcHeading,
  AcRichContent,
  AcCard,
  AcImage,
  AcBadge,
  AcDefinitionList,
} from "@opp/ui-kit";
import { withTranslation } from "react-i18next";
import AcCopyText from "../../../atoms/ac-copy-text/ac-copy-text";
import { AcGetPaymentMethodImg } from "../../../utilities/ac-format-payment-method";
import { AcGetDateIsInFuture } from "../../../utilities/ac-format-date";
import moment from "moment";
import { AcFormatInternalURI } from "../../../utilities";
import { Link } from "react-router-dom";
import {
  GetTransactionsByTransactionUidRefundsAndRefundUidPayoutsApiResponse,
  useGetChargesByUidPayoutsQuery,
  useGetTransactionsByTransactionUidRefundsAndRefundUidPayoutsQuery,
} from "@src/api/openapi/merchantApi";

const _CLASSES = {
  MAIN: "ac-transaction-details-details-tab ac-transaction__grid-container",
  PAYMENT_ICON: "ac-transaction__payment-icon",
};

const AcMovementDetailsDetailsTab = ({
  store: { profile, transactions },
  i18n,
  t,
  data,
}) => {
  const { current_merchant_profiles, current_partner_currency } = profile;

  const {
    is_loading_refund_payouts,
    current_refund_transaction,
    is_loading_refund_transaction,
  } = transactions;
  const { data: payoutData } =
    useGetTransactionsByTransactionUidRefundsAndRefundUidPayoutsQuery(
      {
        transactionUid: current_refund_transaction?.uid,
        refundUid: data.uid,
      },
      {
        skip: !current_refund_transaction || data.object !== KEYS.WITHDRAWAL,
      }
    );
  const { data: chargePayout } = useGetChargesByUidPayoutsQuery(
    {
      uid: data.uid,
    },
    { skip: data.object !== KEYS.INTERNAL_TRANSFER }
  );

  const getPaymentIconClassNames = useMemo(() => {
    return clsx(_CLASSES.PAYMENT_ICON);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  });

  useEffect(() => {
    if (!AcIsSet(data.uid) || !AcIsSet(data.object)) {
      return;
    }
    if (data.object === KEYS.REFUND) {
      transactions.get_refund_payouts(t, data.uid);
    } else {
      transactions.reset(KEYS.REFUND_PAYOUTS);
    }
  }, [data.uid]);

  const getPartnerNameBadge = useMemo(() => {
    if (
      !AcIsSet(current_merchant_profiles) ||
      !AcIsSet(current_merchant_profiles.partner)
    ) {
      return null;
    }

    return (
      <div className={"h-margin-top-5"}>
        <AcBadge theme={THEMES.OMEGA_LIGHT}>
          <span className={"h-text--color-pitch"}>
            {current_merchant_profiles.partner.name}
          </span>
        </AcBadge>
      </div>
    );
  }, [current_merchant_profiles]);

  const getPaymentMethodIcon = useMemo(() => {
    if (
      !AcIsSet(data) ||
      (!AcIsSet(data.payment_method) && !AcIsSet(data.parent_payment_method)) ||
      data.object === KEYS.REFUND
    ) {
      return null;
    }

    return AcGetPaymentMethodImg(
      t,
      getPaymentIconClassNames,
      data.payment_method,
      data.parent_payment_method
    );
  }, [data]);

  const getStatusBadge = useMemo(() => {
    if (!AcIsSet(data)) return null;

    let { status } = data;

    // Only refunds will have status created -> we show them as completed.
    if (status === "created") {
      status = "completed";
    }

    let label = t(`status.${status}`, { ns: "transactions" });
    let theme = null;

    switch (status) {
      case "created":
      case "reserved":
      case "pending":
      case "planned":
        theme = THEMES.OMEGA_LIGHT;
        break;

      case "on_hold":
      case "refunded":
        theme = THEMES.ECHO;
        break;

      case "chargeback":
        theme = THEMES.BETA_LIGHT;
        break;

      case "expired":
      case "cancelled":
      case "failed":
      case "revoked":
        theme = THEMES.ERROR;
        break;

      case "completed":
        theme = THEMES.SUCCESS;
        break;

      default:
    }

    return <AcBadge theme={theme}>{label}</AcBadge>;
  }, [data]);

  const renderMainFeatures = useMemo(() => {
    const { amount, currency, description } = data;

    const content = AcFormatCurrencyAmount(
      amount,
      currency || profile.current_partner_currency,
      i18n.resolvedLanguage,
      true
    );

    return (
      <AcContainer flex className={"ac-transaction__container"}>
        <AcRow className={"h-margin-bottom-auto"}>
          <AcColumn xs={6} sm={6} md={6}>
            <AcHeading
              rank={2}
              theme={THEMES.DARK}
              className={"ac-transaction__amount"}
            >
              {content}
            </AcHeading>
          </AcColumn>

          <AcColumn
            xs={6}
            sm={6}
            md={6}
            className={"h-text--align-right h-flex-h-align-end"}
          >
            {getPartnerNameBadge}
            {getPaymentMethodIcon}
          </AcColumn>

          {AcIsSet(description) && (
            <AcColumn className={"h-hidden-xxs h-hidden-xs h-hidden-sm"}>
              <AcRichContent
                content={`<span class="h-text--color-medium h-text--size-medium">${AcSanitize(
                  description
                )}</span>`}
              />
            </AcColumn>
          )}
        </AcRow>
      </AcContainer>
    );
  }, [getPaymentMethodIcon, getPartnerNameBadge, data, i18n.resolvedLanguage]);

  const renderStatus = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.status))
      return (
        <AcCard className={"ac-transaction__column"}>
          <AcRichContent
            content={`<p><em>${t(
              "no-status-information",
              "No status information available",
              { ns: "transactions" }
            )}</em></p>`}
          />
        </AcCard>
      );

    const { date_settled, updated, status, escrow, category, type, uid } = data;
    let transaction_type = null;
    switch (category) {
      case KEYS.ESCROW:
        transaction_type = t("category.escrow", "Escrow transaction", {
          ns: "transactions",
        });
        break;

      default:
        if (type === KEYS.INCOMING) {
          transaction_type = t("category.incoming", "Incoming transaction", {
            ns: "transactions",
          });
        } else if (type === KEYS.OUTGOING) {
          transaction_type = t("category.outgoing", "Outgoing transaction", {
            ns: "transactions",
          });
        }
    }

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    const result = {
      details: [],
    };

    if (AcIsSet(uid)) {
      result.details.push({
        label: `${t("uid", "Unique identifier", { ns: "common" })}`,
        value: (
          <AcCopyText
            t={t}
            value={uid}
            title={t("copy-uid", "Copy UID to clipboard", { ns: "common" })}
            id={"ac-tra-uid"}
            fixed
          >
            {uid}
          </AcCopyText>
        ),
      });
    }

    result.details.push(
      ...[
        {
          label: `${t("status-label", "Status", { ns: "common" })}`,
          value: getStatusBadge,
        },
        {
          label: `${t("status.completed", "Completed", {
            ns: "transactions",
          })}`,
          value: AcFormatDate(date_settled * 1000, null, date_format),
        },
      ]
    );

    if (
      category &&
      category === KEYS.ESCROW &&
      AcIsSet(escrow.end) &&
      status === KEYS.RESERVED
    ) {
      result.details.push({
        label: `${t("reserved-until", "Reserved until", {
          ns: "transactions",
        })}`,
        value: AcFormatDate(escrow.end * 1000, null, date_format),
      });
    }

    if (AcIsSet(transaction_type)) {
      result.details.push({
        label: `${t("type", "Type", { ns: "common" })}`,
        value: transaction_type,
      });
    }

    return (
      <AcCard className={"ac-transaction__column h-padding-0"}>
        <div
          className={
            "h-padding-right-20 h-padding-top-20 h-padding-bottom-20 transaction-details-wrap"
          }
        >
          <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
            {t("details", "Details", { ns: "common" })}
          </AcHeading>
          <AcDefinitionList items={result.details} />
        </div>
      </AcCard>
    );
  }, [data, getStatusBadge, i18n.resolvedLanguage]);

  const renderPayoutDetail = (
    payoutDetails: GetTransactionsByTransactionUidRefundsAndRefundUidPayoutsApiResponse["data"][0]
  ) => {
    const receiver = [
      {
        label: `<strong>${t("recipient", "Recipient", {
          ns: "transactions",
        })}</strong>`,
        value: "",
      },
      {
        label: `${t("name", "Name", { ns: "transactions" })}`,
        value: payoutDetails.receiver_details?.name,
      },
      {
        label: `${t("account", "Account", { ns: "transactions" })}`,
        value: payoutDetails.receiver_details?.account,
      },
    ];

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let label = t("date-paid", "Date paid", { ns: "transactions" });
    if (payoutDetails.paid) {
      label = AcGetDateIsInFuture(moment(payoutDetails.paid * 1000))
        ? t("date-planned", "Date planned", { ns: "transactions" })
        : t("date-paid", "Date paid", { ns: "transactions" });
    }

    const other = [
      {
        label: `${t("amount", "Amount", { ns: "transactions" })}`,
        value: AcFormatCurrencyAmount(
          payoutDetails.amount,
          payoutDetails.currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: label,
        value: payoutDetails.paid
          ? AcFormatDate(payoutDetails.paid * 1000, null, date_format)
          : t("not-paid-yet", "Not paid yet.", { ns: "transactions" }),
      },
      {
        label: `${t("description", "Description", { ns: "transactions" })}`,
        value: payoutDetails.description,
      },
    ];

    return (
      <AcCard className={"ac-transaction__column"}>
        <AcHeading rank={5} theme={THEMES.PITCH}>
          {t("payout", "Payout", { ns: "transactions" })}
        </AcHeading>
        <AcDefinitionList items={other} />
        {payoutDetails.receiver_details && (
          <AcDefinitionList items={receiver} />
        )}
      </AcCard>
    );
  };

  const renderPayoutDetails = useMemo(() => {
    if (!AcIsSet(payoutData || chargePayout)) {
      return;
    }

    return (
      <>
        {(chargePayout || payoutData).data.map((payout) => {
          return renderPayoutDetail(payout);
        })}
      </>
    );
  }, [
    payoutData,
    chargePayout,
    is_loading_refund_payouts,
    i18n.resolvedLanguage,
  ]);

  const renderImage = useMemo(() => {
    return (
      <AcImage
        wrpClassName={"ac-transaction__hero"}
        source={TransactionHero1x}
        key={AcUUID()}
        transparent
      />
    );
  }, []);

  const renderTransactionData = useMemo(() => {
    if (!AcIsSet(current_refund_transaction) || is_loading_refund_transaction) {
      return;
    }

    const date_format =
      window.outerWidth <= 768
        ? DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES_SHORT
        : DATETIME_FORMATS.DATETIME_WITH_YEAR_AND_MINUTES;

    let statusLabel = `${t("created-at", "Created at", { ns: "common" })}`;
    let statusDate = AcFormatDate(
      current_refund_transaction.created * 1000,
      null,
      date_format
    );

    if (
      current_refund_transaction &&
      current_refund_transaction.statuses &&
      current_refund_transaction.statuses.length > 0
    ) {
      const sorted = current_refund_transaction.statuses.slice().reverse();

      const lastStatus = sorted[0];
      statusLabel = statusLabel = t(
        "status." + current_refund_transaction.status,
        "Status",
        { ns: "transactions" }
      );
      statusDate = AcFormatDate(lastStatus.created * 1000, null, date_format);
    }

    const type = KEYS.TRANSACTION;
    let data = [];
    data[KEYS.UID] = current_refund_transaction.uid;

    const route = AcFormatInternalURI(data, {
      field: KEYS.UID,
      type: type,
    });

    // TODO currency
    const info = [
      {
        label: `${t("amount", "Amount", { ns: "transactions" })}`,
        value: AcFormatCurrencyAmount(
          current_refund_transaction.amount,
          current_partner_currency,
          i18n.resolvedLanguage,
          true
        ),
      },
      {
        label: statusLabel,
        value: statusDate,
      },
      {
        label: `${t("details", { ns: "common" })}`,
        value: (
          <Link className={"ac-link"} target="_blank" to={route}>
            {current_refund_transaction.uid}
            <AcIcon className={"h-margin-x-0"} icon={ICONS.OPEN_IN_NEW} />
          </Link>
        ),
      },
    ];

    return (
      <AcCard className={"ac-transaction__column"}>
        <AcHeading rank={5} theme={THEMES.PITCH}>
          {t("type.transaction", { ns: "transactions" })}
        </AcHeading>
        <AcDefinitionList items={info} />
      </AcCard>
    );
  }, [
    current_refund_transaction,
    is_loading_refund_transaction,
    i18n.resolvedLanguage,
  ]);
  const renderTransactionBreakdown = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.costs)) {
      return (
        <AcRichContent
          content={`<p><em>${t(
            "no-transaction-details",
            "No transaction details available",
            { ns: "transactions" }
          )}</em></p>`}
        />
      );
    }

    const paid_amount = data.amount;
    const transaction_fees = data.costs;
    const received_amount = data.amount - data.costs;

    const formatted = {
      //todo currency
      paid_amount: AcFormatCurrencyAmount(
        paid_amount,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
      transaction_fees: AcFormatCurrencyAmount(
        transaction_fees,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
      received_amount: AcFormatCurrencyAmount(
        received_amount,
        current_partner_currency,
        i18n.resolvedLanguage,
        true
      ),
    };

    const result = [
      {
        label: `${t("paid-amount", "Paid amount", { ns: "transactions" })}`,
        value: formatted.paid_amount,
      },
      {
        label: `${t("transaction-fees", "Transaction fees", {
          ns: "transactions",
        })}`,
        value: formatted.transaction_fees,
      },
      {
        label: `<strong>${t("amount", { ns: "transactions" })}</strong>`,
        value: `<strong>${formatted.received_amount}</strong>`,
      },
    ];
    return <AcDefinitionList items={result} />;
  }, [data, i18n.resolvedLanguage]);

  return (
    <div className={getMainClassNames}>
      <AcCard className={"ac-transaction__column ac-transaction__column--big"}>
        {renderImage}
        {renderMainFeatures}
      </AcCard>

      {renderPayoutDetails}

      {renderStatus}

      {renderTransactionData}
      <AcCard className={"ac-transaction__column"}>
        <AcHeading theme={THEMES.PITCH} rank={5} tag={"h3"}>
          {t("breakdown", "Breakdown", { ns: "transactions" })}
        </AcHeading>

        {renderTransactionBreakdown}
      </AcCard>
    </div>
  );
};

export default withTranslation(["transactions", "titles", "common"])(
  withStore(observer(AcMovementDetailsDetailsTab))
);
